import React, { useState, useEffect } from 'react';
import { MemberModel, StudentCounselModel } from '../../../../models';
import { db } from '../../../../firebase';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { ClipboardList, Plus, Search, MessageSquare } from 'lucide-react';
import StudentCounselCard from './StudentCounselCard';
import StudentCounselDetailView from './StudentCounselDetailView';
import Header from '../studentFrame/header';

interface StudentCounselProps {
    member: MemberModel;
    onAdd: () => void;
}

const StudentCounsel: React.FC<StudentCounselProps> = ({ member, onAdd }) => {
    const [logs, setLogs] = useState<StudentCounselModel[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [selectedLog, setSelectedLog] = useState<StudentCounselModel | null>(null);
    const [searchTerm, setSearchTerm] = useState('');

    useEffect(() => {
        const q = query(
            collection(db, 'studentCounsel'),
            where('memberId', '==', member.id)
        );

        const unsubscribe = onSnapshot(q, (snap) => {
            const list: StudentCounselModel[] = [];
            snap.forEach(doc => list.push({ id: doc.id, ...doc.data() } as StudentCounselModel));
            list.sort((a, b) => b.createdAt - a.createdAt);
            setLogs(list);
            setIsLoading(false);
        }, (err) => {
            console.error("Error fetching counsel logs:", err);
            setIsLoading(false);
        });

        return () => unsubscribe();
    }, [member.id]);

    const filteredLogs = logs.filter(log =>
        log.title.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.content.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.tag.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (isLoading) {
        return (
            <div className="flex flex-col items-center justify-center py-24 gap-4 animate-pulse">
                <div className="w-12 h-12 border-4 border-indigo-100 border-t-indigo-500 rounded-full animate-spin"></div>
                <p className="text-slate-400 font-black text-sm uppercase tracking-widest">상담 기록 불러오는 중...</p>
            </div>
        );
    }

    if (selectedLog) {
        return (
            <StudentCounselDetailView
                log={selectedLog}
                onBack={() => setSelectedLog(null)}
            />
        );
    }

    return (
        <div className="space-y-6 animate-in fade-in duration-500 pb-20 sm:pb-10">
            {/* Header */}
            {/* Header */}
            <Header
                icon={ClipboardList}
                title="상담 및 관찰기록"
                subtitle="선생님과 주고받은 피드백과 소통 내역입니다."
                onButtonClick={onAdd}
                buttonText="상담 요청하기"
                buttonIcon={Plus}
            />

            {/* Search Bar */}
            <div className="relative">
                <Search className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-300" size={16} />
                <input
                    type="text"
                    placeholder="제목, 내용, 태그로 검색..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full bg-white border border-slate-100 rounded-xl pl-11 pr-4 py-3 text-sm font-medium shadow-sm focus:ring-2 focus:ring-indigo-100 outline-none transition-all placeholder:text-slate-300"
                />
            </div>

            {/* Counsel List */}
            {filteredLogs.length > 0 ? (
                <div className="space-y-4">
                    {filteredLogs.map((log) => (
                        <StudentCounselCard
                            key={log.id}
                            log={log}
                            onClick={() => setSelectedLog(log)}
                        />
                    ))}
                </div>
            ) : (
                <div className="py-24 text-center bg-white rounded-[4rem] border border-slate-100 shadow-sm border-dashed">
                    <div className="w-20 h-20 bg-slate-50 rounded-[2rem] flex items-center justify-center mx-auto mb-6 text-slate-200">
                        <MessageSquare size={40} />
                    </div>
                    <h3 className="text-xl font-black text-slate-800">상담 기록이 없습니다</h3>
                    <p className="text-sm font-bold text-slate-400 mt-2">상단의 '상담 요청하기'를 통해 선생님께 문의해보세요!</p>
                </div>
            )}
        </div>
    );
};

export default StudentCounsel;
